import React from 'react';
import { Box, HStack, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';

const RING_COLOR = '#EF4444';
const PILL_BG = '#4E1AEF';

type Props = {
  label: string;
  icon?: React.ReactNode;
  selected: boolean;
  onClick: () => void;
};

// A selectable filter pill: brand purple with a white icon + label. Selection is drawn
// as an inset ring plus a corner marker, so it never changes the pill's footprint or
// gets clipped when pills wrap.
const FilterPill = ({ label, icon, selected, onClick }: Props) => (
  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.96 }} style={{ display: 'inline-flex' }}>
    <HStack
      as="button"
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      position="relative"
      gap="8px"
      paddingX="16px"
      paddingY="10px"
      borderRadius="full"
      backgroundColor={PILL_BG}
      boxShadow={selected ? `inset 0 0 0 2.5px ${RING_COLOR}` : undefined}
      transition="box-shadow 0.15s ease, filter 0.15s ease"
      _hover={{ filter: 'brightness(1.08)' }}
    >
      {icon && (
        <Box display="flex" alignItems="center" justifyContent="center" color="white" flexShrink={0}>
          {icon}
        </Box>
      )}
      <Text fontSize="14px" fontWeight={700} color="white" whiteSpace="nowrap">
        {label}
      </Text>

      {selected && (
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 600, damping: 20 }}
          style={{
            position: 'absolute',
            top: '-3px',
            left: '-3px',
            width: '14px',
            height: '14px',
            borderRadius: '9999px',
            background: RING_COLOR,
            boxShadow: '0 0 0 2px white',
          }}
        />
      )}
    </HStack>
  </motion.div>
);

export default FilterPill;
